/* Developer Portal — Hello by Flow */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "quickstart", label: "Quickstart" },
  { id: "authentication", label: "Authentication" },
  { id: "sdks", label: "Official SDKs" },
  { id: "postman", label: "Postman" },
  { id: "webhooks", label: "Webhooks" },
  { id: "reference", label: "API Reference" },
  { id: "errors", label: "Errors & Status Codes" },
  { id: "rate-limits", label: "Rate Limits & Lockout" },
  { id: "support", label: "Support" },
];

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid="dev-portal-copy-btn"
      onClick={() => {
        navigator.clipboard?.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
      className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md bg-white/5 text-white/70 hover:text-white border border-white/10 hover:bg-white/10 transition"
    >
      {done ? "copied" : "copy"}
    </button>
  );
}

function CodeBlock({ language, children, testid }) {
  return (
    <div className="my-4 rounded-lg overflow-hidden border border-white/10 bg-[#0b0f1a]">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-[#0e1422]">
        <span className="text-[11px] uppercase tracking-wider text-white/50">{language}</span>
        <CopyBtn text={String(children).trim()} />
      </div>
      <pre className="px-4 py-4 text-[13px] leading-relaxed text-white/90 overflow-x-auto" data-testid={testid}>
        <code>{String(children).trim()}</code>
      </pre>
    </div>
  );
}

function Badge({ color = "violet", children }) {
  const palette = {
    violet: "bg-violet-500/15 text-violet-300 border-violet-400/30",
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-400/30",
    sky: "bg-sky-500/15 text-sky-300 border-sky-400/30",
    rose: "bg-rose-500/15 text-rose-300 border-rose-400/30",
  };
  return (
    <span className={`inline-flex items-center text-[11px] font-mono px-2 py-0.5 rounded-md border ${palette[color]}`}>
      {children}
    </span>
  );
}

export default function Developers() {
  const [manifest, setManifest] = useState(null);
  const observerRef = useRef(null);
  const [active, setActive] = useState("overview");

  useEffect(() => {
    fetch(`${BASE}/api/developers/manifest`)
      .then((r) => r.json())
      .then(setManifest)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const sections = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const swaggerHref = `${BASE}/api/docs`;
  const redocHref = `${BASE}/api/redoc`;
  const postmanDownload = `${BASE}/api/developers/postman`;
  const pythonSdkDownload = `${BASE}/api/developers/sdk/python.tar.gz`;
  const tsSdkDownload = `${BASE}/api/developers/sdk/typescript.tar.gz`;
  const openapiDownload = `${BASE}/api/developers/openapi.json`;

  return (
    <div className="min-h-screen bg-[#070b14] text-white" data-testid="dev-portal-root">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#070b14]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-bold text-xl tracking-tight hover:opacity-90" data-testid="dev-portal-logo">
            Hello by Flow<span className="text-violet-400"> · Developers</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <a href={swaggerHref} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg hover:bg-white/5 text-white/80" data-testid="dev-portal-link-swagger">Swagger UI ↗</a>
            <a href={redocHref} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg hover:bg-white/5 text-white/80" data-testid="dev-portal-link-redoc">ReDoc ↗</a>
            <a href={openapiDownload} className="px-3 py-1.5 rounded-lg hover:bg-white/5 text-white/80" data-testid="dev-portal-link-openapi">OpenAPI</a>
            <Link to="/signup" className="ml-3 px-4 py-1.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium" data-testid="dev-portal-cta-signup">
              Get an API key →
            </Link>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 py-10">
        {/* Sidebar */}
        <aside className="hidden lg:block col-span-3">
          <nav className="sticky top-24 space-y-1 text-sm">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`dev-portal-nav-${s.id}`}
                className={`block px-3 py-2 rounded-lg transition border-l-2 ${
                  active === s.id
                    ? "border-violet-400 bg-white/5 text-white"
                    : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                {s.label}
              </a>
            ))}
            {manifest && (
              <div className="mt-6 p-3 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white/60 leading-relaxed">
                <div>API v{manifest.api_version}</div>
                <div>{manifest.endpoint_count} endpoints</div>
                <div className="mt-1 text-white/40">Last regenerated:</div>
                <div className="font-mono text-white/70">{fmtDate(manifest.openapi?.modified_at)}</div>
              </div>
            )}
          </nav>
        </aside>

        {/* Main */}
        <main className="col-span-12 lg:col-span-9 space-y-20" data-testid="dev-portal-main">
          {/* OVERVIEW / HERO */}
          <section id="overview" className="scroll-mt-24">
            <Badge color="violet">v{manifest?.api_version || "1.0.0"}</Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mt-4">
              Build call, SMS and chat features<br />
              on the Hello by Flow API.
            </h1>
            <p className="text-white/70 text-lg leading-relaxed mt-5 max-w-2xl">
              The Hello by Flow API powers a multi-tenant cloud calling platform: external Twilio voice and SMS,
              free internal team chat, peer-to-peer WebRTC meetings, and an admin console for managing
              customer organizations. Production-ready, REST, OpenAPI 3.1.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#quickstart" className="px-5 py-2.5 rounded-lg bg-violet-500 hover:bg-violet-400 font-medium" data-testid="dev-portal-hero-quickstart">
                Start in 60 seconds →
              </a>
              <a href={swaggerHref} target="_blank" rel="noreferrer" className="px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15" data-testid="dev-portal-hero-swagger">
                Browse the API reference
              </a>
              <a href={postmanDownload} className="px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15" data-testid="dev-portal-hero-postman">
                Download Postman
              </a>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-12">
              {[
                { k: "REST + JSON", v: "OpenAPI 3.1 spec" },
                { k: "Cookie or Bearer JWT", v: "Same scheme, two flavors" },
                { k: "Multi-tenant", v: "Org-scoped automatically" },
              ].map((c) => (
                <div key={c.k} className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="text-white/50 text-[12px] uppercase tracking-wider">{c.k}</div>
                  <div className="font-display text-lg mt-1">{c.v}</div>
                </div>
              ))}
            </div>
          </section>

          {/* QUICKSTART */}
          <section id="quickstart" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">Quickstart</h2>
            <p className="text-white/70 leading-relaxed">
              Three steps to your first API call. Use the demo credentials below, or grab your own from{" "}
              <Link to="/signup" className="text-violet-300 underline">/signup</Link>.
            </p>

            <ol className="mt-6 space-y-8 list-none">
              <li>
                <div className="flex items-baseline gap-3">
                  <Badge color="emerald">1</Badge>
                  <h3 className="font-display text-xl font-semibold">Authenticate</h3>
                </div>
                <CodeBlock language="cURL" testid="dev-portal-code-login-curl">{`curl -X POST ${BASE}/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"admin@hellobyflow.com","password":"admin123"}'`}</CodeBlock>
              </li>
              <li>
                <div className="flex items-baseline gap-3">
                  <Badge color="emerald">2</Badge>
                  <h3 className="font-display text-xl font-semibold">Make your first call</h3>
                </div>
                <CodeBlock language="cURL" testid="dev-portal-code-curl-call">{`curl -X POST ${BASE}/api/calls/start \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"to_number":"+13072402137","to_name":"Hana Park","direction":"outbound"}'`}</CodeBlock>
              </li>
              <li>
                <div className="flex items-baseline gap-3">
                  <Badge color="emerald">3</Badge>
                  <h3 className="font-display text-xl font-semibold">Send your first SMS</h3>
                </div>
                <CodeBlock language="Python (SDK)" testid="dev-portal-code-py-sms">{`from hellobyflow import HelloByFlowClient

client = HelloByFlowClient(base_url="${BASE}")
client.login(email="you@example.com", password="your-password")

client.send_sms(body={"to_number": "+13072402137", "body": "Hello!"})`}</CodeBlock>
                <CodeBlock language="TypeScript (SDK)" testid="dev-portal-code-ts-sms">{`import { HelloByFlowClient } from "@hellobyflow/sdk";

const client = new HelloByFlowClient({ baseUrl: "${BASE}" });
await client.login({ email: "you@example.com", password: "your-password" });

await client.sendSms({ body: { to_number: "+13072402137", body: "Hello!" } });`}</CodeBlock>
              </li>
            </ol>
          </section>

          {/* AUTHENTICATION */}
          <section id="authentication" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">Authentication</h2>
            <p className="text-white/70 leading-relaxed">
              Every protected endpoint accepts two equivalent auth schemes. Pick whichever fits
              your runtime — the server treats them identically.
            </p>
            <div className="grid md:grid-cols-2 gap-5 mt-6">
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Badge color="sky">cookieAuth</Badge>
                  <span className="text-white/50 text-sm">Browser apps</span>
                </div>
                <p className="text-sm text-white/70 leading-relaxed">
                  <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">POST /api/auth/login</code> sets an
                  HttpOnly <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">access_token</code> cookie
                  (12h TTL) automatically — no client-side token handling needed for same-site requests.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Badge color="violet">bearerAuth (JWT)</Badge>
                  <span className="text-white/50 text-sm">Servers, CLIs, mobile</span>
                </div>
                <p className="text-sm text-white/70 leading-relaxed">
                  Read <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">access_token</code> from the
                  login response and send <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">Authorization: Bearer &lt;jwt&gt;</code>{" "}
                  on every protected request. The token encodes user id, email and role.
                </p>
              </div>
            </div>

            <h3 className="font-display text-xl font-semibold mt-8 mb-2">Roles</h3>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="min-w-full text-sm">
                <thead className="bg-white/5 text-white/60">
                  <tr>
                    <th className="text-left px-4 py-2">Role</th>
                    <th className="text-left px-4 py-2">Visibility</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr><td className="px-4 py-2 font-mono text-violet-300">owner</td><td className="px-4 py-2 text-white/80">Cross-org. Runs <code>/api/owner/*</code> platform console.</td></tr>
                  <tr><td className="px-4 py-2 font-mono text-sky-300">admin</td><td className="px-4 py-2 text-white/80">Full control of own organization.</td></tr>
                  <tr><td className="px-4 py-2 font-mono text-emerald-300">supervisor</td><td className="px-4 py-2 text-white/80">Read-only across org (calls, recordings, analytics).</td></tr>
                  <tr><td className="px-4 py-2 font-mono text-white/80">agent</td><td className="px-4 py-2 text-white/80">Only their own contacts / calls / SMS.</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* SDKS */}
          <section id="sdks" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">Official SDKs</h2>
            <p className="text-white/70 leading-relaxed">
              Both SDKs are auto-generated from <a className="text-violet-300 underline" href={openapiDownload}>the same OpenAPI spec</a>{" "}
              the server publishes — they regenerate on every backend boot, so they never drift from the API.
            </p>

            <div className="grid md:grid-cols-2 gap-5 mt-6">
              <div className="p-6 rounded-xl bg-gradient-to-br from-violet-500/10 to-sky-500/5 border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-xl font-bold">Python</h3>
                  <Badge color="emerald">httpx</Badge>
                </div>
                <p className="text-sm text-white/70 mb-4">{manifest?.sdk_python?.exists ? `${fmtBytes(manifest.sdk_python.size_bytes)} · ${fmtDate(manifest.sdk_python.modified_at)}` : "—"}</p>
                <a href={pythonSdkDownload} className="inline-flex items-center px-4 py-2 rounded-lg bg-white text-[#070b14] font-medium hover:bg-white/90" data-testid="dev-portal-download-py">
                  Download .tar.gz
                </a>
                <CodeBlock language="install" testid="dev-portal-py-install">{`curl -L -o hbf-sdk-python.tar.gz ${pythonSdkDownload}
tar xzf hbf-sdk-python.tar.gz
cd hbf-sdk-python && pip install -e .`}</CodeBlock>
              </div>
              <div className="p-6 rounded-xl bg-gradient-to-br from-sky-500/10 to-emerald-500/5 border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-xl font-bold">TypeScript</h3>
                  <Badge color="sky">fetch</Badge>
                </div>
                <p className="text-sm text-white/70 mb-4">{manifest?.sdk_typescript?.exists ? `${fmtBytes(manifest.sdk_typescript.size_bytes)} · ${fmtDate(manifest.sdk_typescript.modified_at)}` : "—"}</p>
                <a href={tsSdkDownload} className="inline-flex items-center px-4 py-2 rounded-lg bg-white text-[#070b14] font-medium hover:bg-white/90" data-testid="dev-portal-download-ts">
                  Download .tar.gz
                </a>
                <CodeBlock language="install" testid="dev-portal-ts-install">{`curl -L -o hbf-sdk-typescript.tar.gz ${tsSdkDownload}
tar xzf hbf-sdk-typescript.tar.gz
cd hbf-sdk-typescript && npm install && npm run build`}</CodeBlock>
              </div>
            </div>

            <p className="mt-6 text-white/60 text-sm">
              Need a Ruby, Go, or PHP client?{" "}
              <a className="text-violet-300 underline" href={openapiDownload}>Feed the OpenAPI spec</a>{" "}
              into <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">openapi-generator-cli</code>{" "}
              or your favorite generator — the spec has rich tags, summaries, and examples ready.
            </p>
          </section>

          {/* POSTMAN */}
          <section id="postman" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">Postman Collection</h2>
            <p className="text-white/70 leading-relaxed">
              One-click collection with every endpoint pre-organized by tag. Set the{" "}
              <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">email</code> and{" "}
              <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">password</code> environment variables
              once — the included pre-request script auto-fetches an access token before every request.
            </p>
            <div className="mt-5 flex flex-wrap gap-3 items-center">
              <a href={postmanDownload} className="inline-flex items-center px-4 py-2 rounded-lg bg-white text-[#070b14] font-medium hover:bg-white/90" data-testid="dev-portal-download-postman">
                Download collection
              </a>
              <span className="text-white/50 text-sm">
                {manifest?.postman_collection?.exists ? `${fmtBytes(manifest.postman_collection.size_bytes)} · ${fmtDate(manifest.postman_collection.modified_at)}` : "—"}
              </span>
            </div>
          </section>

          {/* WEBHOOKS */}
          <section id="webhooks" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">Webhooks</h2>
            <p className="text-white/70 leading-relaxed">
              Hello by Flow exposes Twilio&apos;s standard callback endpoints so you can self-host PSTN routing.
              All webhook URLs are public (no auth) — Twilio signs requests with{" "}
              <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">X-Twilio-Signature</code>;
              we verify it via <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">providers/twilio.validate_signature()</code>.
            </p>
            <div className="overflow-x-auto rounded-xl border border-white/10 mt-5">
              <table className="min-w-full text-sm">
                <thead className="bg-white/5 text-white/60">
                  <tr>
                    <th className="text-left px-4 py-2">Event</th>
                    <th className="text-left px-4 py-2">URL (POST)</th>
                    <th className="text-left px-4 py-2">Effect</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr><td className="px-4 py-2"><Badge color="violet">voice</Badge></td><td className="px-4 py-2 font-mono text-violet-300">/api/twilio/voice</td><td className="px-4 py-2 text-white/80">TwiML — rings agents (inbound) or dials out (outbound).</td></tr>
                  <tr><td className="px-4 py-2"><Badge color="violet">voice-inbound</Badge></td><td className="px-4 py-2 font-mono text-violet-300">/api/twilio/voice-inbound</td><td className="px-4 py-2 text-white/80">Legacy inbound for older purchased numbers.</td></tr>
                  <tr><td className="px-4 py-2"><Badge color="amber">status</Badge></td><td className="px-4 py-2 font-mono text-amber-300">/api/twilio/status</td><td className="px-4 py-2 text-white/80">Updates final status, duration, ended_at.</td></tr>
                  <tr><td className="px-4 py-2"><Badge color="emerald">recording</Badge></td><td className="px-4 py-2 font-mono text-emerald-300">/api/twilio/recording</td><td className="px-4 py-2 text-white/80">Persists the recording URL + duration.</td></tr>
                  <tr><td className="px-4 py-2"><Badge color="sky">sms-inbound</Badge></td><td className="px-4 py-2 font-mono text-sky-300">/api/twilio/sms-inbound</td><td className="px-4 py-2 text-white/80">Stores inbound SMS, auto-links contact by phone.</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-white/60 text-sm mt-4">
              Pointing your own Twilio account at these URLs? Replace <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">{BASE.replace("https://", "")}</code> with your custom subdomain.
            </p>
          </section>

          {/* REFERENCE */}
          <section id="reference" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">API Reference</h2>
            <p className="text-white/70 leading-relaxed mb-6">
              The full operation reference — every endpoint with examples, response schemas, and a &quot;Try it out&quot; button.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <a href={swaggerHref} target="_blank" rel="noreferrer" className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-violet-400/40 hover:bg-white/[0.07] transition" data-testid="dev-portal-card-swagger">
                <div className="font-display font-bold text-lg">Swagger UI ↗</div>
                <p className="text-white/60 text-sm mt-1">Interactive — sign in once and execute requests live.</p>
              </a>
              <a href={redocHref} target="_blank" rel="noreferrer" className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-violet-400/40 hover:bg-white/[0.07] transition" data-testid="dev-portal-card-redoc">
                <div className="font-display font-bold text-lg">ReDoc ↗</div>
                <p className="text-white/60 text-sm mt-1">Three-panel reference — best for reading top-to-bottom.</p>
              </a>
            </div>
          </section>

          {/* ERRORS */}
          <section id="errors" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">Errors & Status Codes</h2>
            <p className="text-white/70 leading-relaxed">
              Every error uses the same JSON envelope:
            </p>
            <CodeBlock language="JSON" testid="dev-portal-code-err">{`{ "detail": "Admin only" }`}</CodeBlock>
            <div className="overflow-x-auto rounded-xl border border-white/10 mt-4">
              <table className="min-w-full text-sm">
                <thead className="bg-white/5 text-white/60">
                  <tr>
                    <th className="text-left px-4 py-2">Code</th>
                    <th className="text-left px-4 py-2">Meaning</th>
                    <th className="text-left px-4 py-2">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr><td className="px-4 py-2 font-mono">400</td><td className="px-4 py-2">Bad request</td><td className="px-4 py-2 text-white/70">Validation or business rule violation.</td></tr>
                  <tr><td className="px-4 py-2 font-mono">401</td><td className="px-4 py-2">Unauthorized</td><td className="px-4 py-2 text-white/70">Missing or invalid token / cookie.</td></tr>
                  <tr><td className="px-4 py-2 font-mono">403</td><td className="px-4 py-2">Forbidden</td><td className="px-4 py-2 text-white/70">Authenticated but lacks the role/scope.</td></tr>
                  <tr><td className="px-4 py-2 font-mono">404</td><td className="px-4 py-2">Not found</td><td className="px-4 py-2 text-white/70">Resource missing or not visible to you.</td></tr>
                  <tr><td className="px-4 py-2 font-mono">422</td><td className="px-4 py-2">Unprocessable Entity</td><td className="px-4 py-2 text-white/70">Pydantic body failed schema validation.</td></tr>
                  <tr><td className="px-4 py-2 font-mono">429</td><td className="px-4 py-2">Too Many Requests</td><td className="px-4 py-2 text-white/70">Brute-force lockout — 5 failed logins / 15 min.</td></tr>
                  <tr><td className="px-4 py-2 font-mono">503</td><td className="px-4 py-2">Service Unavailable</td><td className="px-4 py-2 text-white/70">A dependency (Twilio, storage, LLM) is down.</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* RATE LIMITS */}
          <section id="rate-limits" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">Rate Limits & Lockout</h2>
            <p className="text-white/70 leading-relaxed">
              We don&apos;t currently enforce a global request quota. Two specific protections are in place:
            </p>
            <ul className="mt-4 space-y-2 text-white/80 list-disc list-inside">
              <li>Login lockout: 5 failed attempts on the same identifier triggers a 15-minute cooldown returning <code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">429</code>.</li>
              <li>Attachment uploads are capped at <strong>25 MB</strong> per file (<code className="bg-white/10 px-1.5 py-0.5 rounded text-[12px]">413</code> if exceeded).</li>
            </ul>
            <p className="text-white/60 text-sm mt-4">Need higher limits? Email <a className="text-violet-300 underline" href="mailto:partners@hellobyflow.com">partners@hellobyflow.com</a>.</p>
          </section>

          {/* SUPPORT */}
          <section id="support" className="scroll-mt-24">
            <h2 className="font-display text-3xl font-bold mb-3">Support</h2>
            <p className="text-white/70 leading-relaxed">
              Stuck on an integration? Two ways to reach us — both real humans.
            </p>
            <div className="grid sm:grid-cols-2 gap-5 mt-6">
              <a href="mailto:partners@hellobyflow.com" className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-violet-400/40 transition" data-testid="dev-portal-support-email">
                <div className="font-display font-bold text-lg">Email</div>
                <p className="text-white/60 text-sm mt-1">partners@hellobyflow.com — 24h response.</p>
              </a>
              <Link to="/" state={{ openSupport: true }} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-violet-400/40 transition" data-testid="dev-portal-support-chat">
                <div className="font-display font-bold text-lg">Chat with Vox AI</div>
                <p className="text-white/60 text-sm mt-1">Instant answers to product, pricing, and integration questions.</p>
              </Link>
            </div>
          </section>

          {/* Footer */}
          <footer className="pt-10 border-t border-white/10 text-white/40 text-sm flex flex-wrap items-center justify-between gap-3">
            <div>Hello by Flow API · v{manifest?.api_version || "1.0.0"} · {manifest?.endpoint_count || "—"} endpoints</div>
            <div className="flex items-center gap-4">
              <Link to="/legal/terms" className="hover:text-white">Terms</Link>
              <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/legal/aup" className="hover:text-white">AUP</Link>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
